import React from 'react';
import { Box, Button, HStack, IBoxProps, Skeleton, VStack } from 'native-base';
import { Reward, RewardGrant, useI18n } from '@springbig/shared';

import { Caption, H3 } from './Typography';
import Image from './Image';
import { useOffersStyles, useGeneralStyles } from './styles';
import { SpIcon } from './Icon';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

type DesiredAction = {
    id: number;
    name: string;
    rewards: Reward[];
    type_id: number;
}

type RewardProps = {
  offer?: RewardGrant
  handleAcceptRedeemReward?: (id: number, title: string) => void;
  handleBonusRewards?: (id: number) => void;
  rewardActiveId?: number | undefined;
  loadingActivation?: boolean;
  titleIcon: IconProp;
  iconTitle: string;
  leftIcon: IconProp;
  bodyText: string | undefined;
  subTitle: string;
}

type DesiredActionProps = {
  desiredAction? : DesiredAction;
  onGetOffer?: (typeId: number) => void;
  titleIcon: IconProp;
  iconTitle: string;
  leftIcon: IconProp;
  bodyText: string | undefined;
  subTitle: string;
}

export interface RewardGrantCardProps extends IBoxProps {
  prop: {
    reward?: RewardProps
    desiredAction?: DesiredActionProps
  }
  type: 'reward' | 'desiredAction'
}

const RewardGrantCard = ({
    prop,
    type
}: RewardGrantCardProps) => {

    const { t } = useI18n();
    const { text, icon, button_earn_background, button_earn_text } = useOffersStyles();
    const gStyles = useGeneralStyles();

    return (
        <VStack
            bg={gStyles.section_background}
            space={2}
            mb={2}
            py={4}
            alignItems="center"
            w="100%"
        >
            <HStack space={2}>
                {prop[type]?.titleIcon && <SpIcon color={icon} icon={prop[type]?.titleIcon} />}
                <Caption
                    color={text}
                    fontWeight="bold"
                    textAlign="center"
                    textTransform="capitalize"
                >
                    {prop[type]?.iconTitle.replace('_', ' ')}
                </Caption>
            </HStack>
            <HStack
                space={2}
                w="100%"
                alignItems="center"
                justifyContent="space-evenly"
            >
                <Box
                    w={24}
                    h={24}
                    borderRadius={12}
                    bg={gStyles.screen_background}
                    alignItems="center"
                    justifyContent="center"
                >
                    {
                        prop[type] &&
                        type === 'reward' &&
                        prop[type].offer?.reward &&
                        prop[type].offer?.reward?.images?.length > 0 ? (
                            <Image
                            w={"24"}
                            h={"24"}
                            p={3}
                            borderRadius={12}
                            resizeMode="cover"
                            uri={`${prop[type].offer?.reward?.images[0].url}`}
                            />
                        ) : prop[type]?.leftIcon ? (
                            <SpIcon color={icon} icon={prop[type]?.leftIcon} size={80} />
                        ) : (
                            <></>
                        )
                    }
                </Box>
                <Box width="45%" alignSelf="center">
                    <H3
                        color={text}
                        textAlign="center"
                        textOverflow="ellipsis"
                        numberOfLines={2}
                    >
                        {prop[type]?.bodyText}
                    </H3>
                    <Caption textAlign={'center'}>
                        {prop[type]?.subTitle}
                    </Caption>
                </Box>
                {type === 'reward' && (
                    <VStack w="20%">
                        {prop[type]?.offer?._type === 'bonus' ? (
                            <Button
                                bg={
                                    prop[type].offer.active
                                        ? button_earn_background
                                        : gStyles.button_background
                                }
                                _text={{
                                    color:
                                        prop[type].offer.active
                                            ? button_earn_text
                                            : gStyles.button_text,
                                }}
                                isDisabled={
                                    prop[type].offer?.redemptions_available === 0 || prop[type].loadingActivation || prop[type].offer?.active
                                }
                                h={50}
                                onPress={() => {

                                    if (prop[type]?.offer?.active || prop[type]?.loadingActivation) {
                                        return;
                                    }

                                    if (prop[type]?.handleBonusRewards) {
                                        prop[type].handleBonusRewards(prop[type]?.offer?.id);
                                    }
                                }}
                            >
                                {prop[type].offer.active ? 'Earning' : 'Use'}
                            </Button>
                        ) : (
                            <Button
                                testID="get-button"
                                bg={gStyles.button_background}
                                _text={{
                                    color: gStyles.button_text,
                                }}
                                isDisabled={prop[type]?.offer?.redemptions_available === 0}
                                h={50}
                                onPress={() => {

                                    if (prop[type]?.handleAcceptRedeemReward) {
                                        prop[type].handleAcceptRedeemReward(prop[type]?.offer?.id, prop[type]?.offer?.reward.name);
                                    }
                                }
                                }
                            >
                                {prop[type]?.offer._type === 'gift_card' ? t('giftCard.claim') : t('general.get')}
                            </Button>
                        )}
                        <Box>
                            {prop[type]?.offer?.redemptions_available === 0 ? (
                                <Caption
                                    textAlign="center"
                                    color={text}
                                    text={'general.redeemed'}
                                />
                            ) : (
                                <Caption color={text} textAlign="center">
                                    {prop[type]?.offer?.redemptions_available === -1
                                        ? 'unlimited'
                                        : `use ${prop[type]?.offer?.redemptions_available}x`}
                                </Caption>
                            )}
                        </Box>
                    </VStack>
                )}
                {type === 'desiredAction' && (
                    <Button
                        testID="get-button"
                        bg={gStyles.button_background}
                        _text={{
                            color: gStyles.button_text,
                        }}
                        h={50}
                        w={'20%'}
                        onPress={() => {

                            if (prop[type]?.onGetOffer && prop[type].desiredAction?.type_id) {
                                prop[type]?.onGetOffer(prop[type]?.desiredAction?.type_id);
                            }
                        }}
                    >
                        {t('offers.add')}
                    </Button>
                )}
            </HStack>
            {type === 'desiredAction' && (
                <Caption
                    textAlign="center"
                    text="offers.desirable_action"
                    textValues={[
                        prop[type]?.bodyText ?? '',
                        `${prop[type]?.desiredAction?.rewards[0]?.name ?? ''}`,
                    ]}
                />
            )}
        </VStack>
    );
};

export const RewardGrantCardSkeleton = () => {

    const gStyles = useGeneralStyles();

    return (
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} w='100%' my={2} bg={gStyles.section_background} py={4}>
            <Box w='100%' display={'flex'} flexDirection={'row'} justifyContent={'center'}>
                <Skeleton.Text lines={1} w={3} mr={2} />
                <Skeleton.Text lines={1} w='40%' />
            </Box>
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} my={1} w='100%' justifyContent={'space-between'} px={7}>
                <Skeleton h={65} w={65} borderRadius={5} />
                <Skeleton.Text lines={1} w='50%' />
                <Box  display={'flex'} flexDirection={'column'} justifyContent={'flex-end'}>
                    <Skeleton h={45} w={85} borderRadius={5} />
                    <Skeleton.Text lines={1} w='70%' pt={2} alignSelf={'center'}/>
                </Box>
            </Box>

            <Skeleton.Text lines={1} w='40%' />
        </Box>
    );
};

export default RewardGrantCard;
